import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import {
  campaigns as mockCampaigns,
  campaignTypeLabels,
  campaignStatusLabels,
  campaignTypeIcon,
  audienceTypeLabels,
  plans,
  radioStatuses,
  radioStatusLabels,
  countries,
  type Campaign,
  type CampaignType,
  type CampaignStatus,
  type AudienceType,
  type AudienceConfig,
} from '@/mocks/superAdminMarketing';

type LoadingState = 'loading' | 'ready' | 'error';

function simulateLoad(): Promise<Campaign[]> {
  return new Promise((resolve, reject) => {
    const delay = 800 + Math.random() * 500;
    setTimeout(() => {
      if (Math.random() > 0.93) reject(new Error('Timeout al conectar con el servidor.'));
      else resolve(mockCampaigns);
    }, delay);
  });
}

function showAlert(icon: 'success' | 'error' | 'warning' | 'info', title: string, text?: string) {
  return Swal.fire({
    icon,
    title,
    text: text ?? '',
    confirmButtonColor: '#1a1a1a',
    customClass: {
      popup: 'rounded-2xl text-sm',
      title: 'text-sm font-bold',
      confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
    },
  });
}

const typeColorMap: Record<CampaignType, { bg: string; text: string }> = {
  upgrade_plan: { bg: 'bg-emerald-50', text: 'text-emerald-500' },
  frecuencia_premium: { bg: 'bg-violet-50', text: 'text-violet-500' },
  licencia_anual: { bg: 'bg-sky-50', text: 'text-sky-500' },
  storage_extra: { bg: 'bg-amber-50', text: 'text-amber-500' },
  app_individual: { bg: 'bg-rose-50', text: 'text-rose-500' },
  reactivacion: { bg: 'bg-teal-50', text: 'text-teal-500' },
  stream_caido: { bg: 'bg-red-50', text: 'text-red-500' },
  evento_especial: { bg: 'bg-orange-50', text: 'text-orange-500' },
};

const allTypes: CampaignType[] = ['upgrade_plan', 'frecuencia_premium', 'licencia_anual', 'storage_extra', 'app_individual', 'reactivacion', 'stream_caido', 'evento_especial'];
const allStatuses: CampaignStatus[] = ['draft', 'scheduled', 'active', 'completed', 'paused'];

export default function SuperAdminMarketing() {
  const [data, setData] = useState<Campaign[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [activeType, setActiveType] = useState('todos');
  const [activeStatus, setActiveStatus] = useState('todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [metricsCampaign, setMetricsCampaign] = useState<Campaign | null>(null);
  const [form, setForm] = useState({
    name: '',
    type: 'upgrade_plan' as CampaignType,
    audienceType: 'all' as AudienceType,
    audiencePlan: '',
    audienceStatus: '',
    audienceCountry: '',
    body: '',
    scheduledAt: '',
  });

  const loadData = useCallback(() => {
    setLoadingState('loading');
    setErrorMsg(null);
    simulateLoad()
      .then((d) => {
        setData(d);
        setLoadingState('ready');
      })
      .catch((err) => {
        setErrorMsg(err.message || 'Error desconocido');
        setLoadingState('error');
      });
  }, []);
  useEffect(() => {
    loadData();
  }, [loadData]);

  const typeCounts = useMemo(() => {
    const c: Record<string, number> = { todos: data.length };
    allTypes.forEach((t) => {
      c[t] = data.filter((cam) => cam.type === t).length;
    });
    return c;
  }, [data]);

  const statusCounts = useMemo(() => {
    const c: Record<string, number> = { todos: data.length };
    allStatuses.forEach((s) => {
      c[s] = data.filter((cam) => cam.status === s).length;
    });
    return c;
  }, [data]);

  const filtered = useMemo(() => {
    let r = data;
    if (activeType !== 'todos') r = r.filter((c) => c.type === activeType);
    if (activeStatus !== 'todos') r = r.filter((c) => c.status === activeStatus);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      r = r.filter((c) => c.name.toLowerCase().includes(q) || c.body.toLowerCase().includes(q) || c.audience.label.toLowerCase().includes(q));
    }
    return r;
  }, [data, activeType, activeStatus, searchTerm]);

  const kpis = useMemo(() => {
    const active = data.filter((c) => c.status === 'active').length;
    const totalSent = data.reduce((s, c) => s + c.sentCount, 0);
    const totalOpened = data.reduce((s, c) => s + c.openedCount, 0);
    const totalClicks = data.reduce((s, c) => s + c.clicks, 0);
    const totalConverted = data.reduce((s, c) => s + c.convertedCount, 0);
    return { active, totalSent, totalOpened, totalClicks, totalConverted };
  }, [data]);

  const buildAudienceLabel = (audienceType: AudienceType, plan: string, status: string, country: string): string => {
    switch (audienceType) {
      case 'all': return 'Todas las radios (48)';
      case 'plan': return `Plan ${plan} (${plan === 'Starter' ? 12 : plan === 'Básico' ? 8 : plan === 'Pro' ? 18 : plan === 'Business' ? 5 : 5})`;
      case 'status': return `${radioStatusLabels[status] || status} (${status === 'active' ? 32 : status === 'stream_down' ? 4 : status === 'suspended' ? 6 : status === 'pending' ? 3 : 3})`;
      case 'country': return `${country || 'Argentina'} (15)`;
      case 'specific': return 'Radios específicas (selección manual)';
      default: return 'Todas las radios (48)';
    }
  };

  const handleStatusChange = (camp: Campaign, newStatus: CampaignStatus) => {
    setData((prev) => prev.map((c) => (c.id === camp.id ? { ...c, status: newStatus } : c)));
    showAlert('success', 'Estado actualizado', `"${camp.name}" ahora está "${campaignStatusLabels[newStatus]}".`);
  };

  const handleDelete = (camp: Campaign) => {
    Swal.fire({
      icon: 'warning',
      title: '¿Eliminar campaña?',
      text: `Vas a eliminar "${camp.name}". Esta acción no se puede deshacer.`,
      showCancelButton: true,
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#e5e5e5',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
        cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700',
      },
    }).then((r) => {
      if (r.isConfirmed) {
        setData((prev) => prev.filter((c) => c.id !== camp.id));
        showAlert('success', 'Eliminada', `"${camp.name}" fue eliminada.`);
      }
    });
  };

  const handleDuplicate = (camp: Campaign) => {
    const newId = Math.max(...data.map((c) => c.id), 0) + 1;
    const dup: Campaign = {
      ...camp,
      id: newId,
      name: `${camp.name} (copia)`,
      status: 'draft' as CampaignStatus,
      sentCount: 0,
      openedCount: 0,
      clicks: 0,
      convertedCount: 0,
      createdAt: new Date().toISOString().slice(0, 10),
      scheduledAt: null,
    };
    setData((prev) => [dup, ...prev]);
    showAlert('success', 'Campaña duplicada', `"${dup.name}" fue creada como borrador.`);
  };

  const handlePause = (camp: Campaign) => {
    Swal.fire({
      icon: 'warning',
      title: '¿Pausar campaña?',
      text: `"${camp.name}" se pausará y no se enviarán más mensajes.`,
      showCancelButton: true,
      confirmButtonText: 'Pausar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d97706',
      cancelButtonColor: '#e5e5e5',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
        cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700',
      },
    }).then((r) => {
      if (r.isConfirmed) {
        handleStatusChange(camp, 'paused');
      }
    });
  };

  const handleActivate = (camp: Campaign) => {
    Swal.fire({
      icon: 'info',
      title: '¿Activar campaña?',
      text: `"${camp.name}" se activará y comenzará a enviarse.`,
      showCancelButton: true,
      confirmButtonText: 'Activar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#059669',
      cancelButtonColor: '#e5e5e5',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
        cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700',
      },
    }).then((r) => {
      if (r.isConfirmed) {
        handleStatusChange(camp, 'active');
      }
    });
  };

  const openEdit = (camp: Campaign) => {
    setEditingCampaign(camp);
    setForm({
      name: camp.name,
      type: camp.type,
      audienceType: camp.audience.type,
      audiencePlan: camp.audience.plan ?? '',
      audienceStatus: camp.audience.status ?? '',
      audienceCountry: camp.audience.country ?? '',
      body: camp.body,
      scheduledAt: camp.scheduledAt ?? '',
    });
    setShowForm(true);
  };

  const openCreate = () => {
    setEditingCampaign(null);
    setForm({
      name: '',
      type: 'upgrade_plan',
      audienceType: 'all',
      audiencePlan: '',
      audienceStatus: '',
      audienceCountry: '',
      body: '',
      scheduledAt: '',
    });
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (!form.name.trim()) {
      showAlert('warning', 'Nombre requerido', 'Poné un nombre a la campaña.');
      return;
    }
    if (!form.body.trim()) {
      showAlert('warning', 'Contenido requerido', 'Escribí el cuerpo del mensaje.');
      return;
    }

    const audienceLabel = buildAudienceLabel(form.audienceType, form.audiencePlan, form.audienceStatus, form.audienceCountry);
    const audienceConfig: AudienceConfig = {
      type: form.audienceType,
      plan: form.audiencePlan || undefined,
      status: form.audienceStatus || undefined,
      country: form.audienceCountry || undefined,
      label: audienceLabel,
    };

    if (editingCampaign) {
      setData((prev) =>
        prev.map((c) =>
          c.id === editingCampaign.id
            ? { ...c, name: form.name, type: form.type, audience: audienceConfig, body: form.body, scheduledAt: form.scheduledAt || null }
            : c,
        ),
      );
      showAlert('success', 'Campaña actualizada', `"${form.name}" fue editada correctamente.`);
    } else {
      const newId = Math.max(...data.map((c) => c.id), 0) + 1;
      const created: Campaign = {
        id: newId,
        name: form.name,
        type: form.type,
        status: 'draft',
        audience: audienceConfig,
        targetCount: 0,
        sentCount: 0,
        openedCount: 0,
        clicks: 0,
        convertedCount: 0,
        createdAt: new Date().toISOString().slice(0, 10),
        scheduledAt: form.scheduledAt || null,
        body: form.body,
      };
      setData((prev) => [created, ...prev]);
      showAlert('success', 'Campaña creada', `"${created.name}" fue creada como borrador.`);
    }
    setShowForm(false);
  };

  const statusBadge = (s: CampaignStatus) => {
    const map: Record<CampaignStatus, string> = {
      draft: 'bg-background-200 text-foreground-500',
      scheduled: 'bg-sky-50 text-sky-600',
      active: 'bg-emerald-50 text-emerald-600',
      completed: 'bg-accent-50 text-accent-600',
      paused: 'bg-amber-50 text-amber-600',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${map[s]}`}>
        {campaignStatusLabels[s]}
      </span>
    );
  };

  const openRate = (c: Campaign) => (c.sentCount > 0 ? ((c.openedCount / c.sentCount) * 100).toFixed(0) : 0);
  const clickRate = (c: Campaign) => (c.sentCount > 0 ? ((c.clicks / c.sentCount) * 100).toFixed(0) : 0);
  const convRate = (c: Campaign) => (c.sentCount > 0 ? ((c.convertedCount / c.sentCount) * 100).toFixed(0) : 0);

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">
                Dashboard
              </Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Marketing</span>
            </div>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Marketing y Campañas</h2>
                <p className="text-foreground-400 text-xs mt-0.5">
                  {loadingState === 'ready'
                    ? `${filtered.length} de ${data.length} campañas · ${kpis.active} activas`
                    : 'Cargando...'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative w-full sm:w-52">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar campañas..."
                    disabled={loadingState !== 'ready'}
                    className="w-full pl-8 pr-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors disabled:opacity-50"
                  />
                </div>
                <button
                  onClick={openCreate}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line text-sm"></i>
                  Nueva Campaña
                </button>
              </div>
            </div>

            {/* KPI Cards */}
            {loadingState === 'ready' && (
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {[
                  { label: 'Activas', value: kpis.active, icon: 'ri-megaphone-line', color: 'text-emerald-500', bg: 'bg-emerald-50' },
                  { label: 'Enviados', value: kpis.totalSent, icon: 'ri-send-plane-line', color: 'text-sky-500', bg: 'bg-sky-50' },
                  { label: 'Abiertos', value: kpis.totalOpened, icon: 'ri-mail-open-line', color: 'text-amber-500', bg: 'bg-amber-50' },
                  { label: 'Clicks', value: kpis.totalClicks, icon: 'ri-cursor-line', color: 'text-violet-500', bg: 'bg-violet-50' },
                  { label: 'Conversiones', value: kpis.totalConverted, icon: 'ri-checkbox-circle-line', color: 'text-accent-500', bg: 'bg-accent-50' },
                ].map((s) => (
                  <div
                    key={s.label}
                    className="bg-background-50 border border-background-200 rounded-xl p-3 flex items-center gap-3"
                  >
                    <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center shrink-0`}>
                      <i className={`${s.icon} ${s.color} text-sm`}></i>
                    </div>
                    <div>
                      <p className="text-foreground-400 text-[10px] font-medium">{s.label}</p>
                      <p className="text-foreground-800 text-sm font-bold">{s.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Loading skeleton */}
            {loadingState === 'loading' && (
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="bg-background-50 border border-background-200 rounded-xl p-3 flex items-center gap-3 animate-pulse">
                    <div className="w-9 h-9 rounded-lg bg-background-200"></div>
                    <div className="space-y-1">
                      <div className="w-16 h-2.5 bg-background-200 rounded"></div>
                      <div className="w-10 h-3 bg-background-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Error */}
            {loadingState === 'error' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
                  <i className="ri-error-warning-line text-red-400 text-2xl"></i>
                </div>
                <p className="text-foreground-800 text-sm font-semibold mb-1">Error</p>
                <p className="text-foreground-400 text-xs mb-4">{errorMsg}</p>
                <button
                  onClick={loadData}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer"
                >
                  <i className="ri-refresh-line text-sm"></i>
                  Reintentar
                </button>
              </div>
            )}

            {/* Info Banner */}
            {loadingState === 'ready' && (
              <div className="bg-accent-50 border border-accent-200 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0">
                    <i className="ri-megaphone-line text-accent-500 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-accent-800 text-xs font-semibold">Centro de Campañas SaaS</p>
                    <p className="text-accent-600/80 text-[10px]">
                      Creá campañas segmentadas por audiencia para venta, retención y upgrades. Datos mock.
                    </p>
                  </div>
                </div>
                <span className="px-2.5 py-1 rounded-full bg-accent-200 text-accent-700 text-[10px] font-medium whitespace-nowrap flex items-center gap-1">
                  <i className="ri-information-line text-[9px]"></i>
                  Modo simulación
                </span>
              </div>
            )}

            {/* Filters */}
            {loadingState === 'ready' && (
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap gap-1">
                  <span className="text-foreground-400 text-[10px] font-medium self-center mr-1">Tipo:</span>
                  {['todos', ...allTypes].map((t) => (
                    <button
                      key={t}
                      onClick={() => setActiveType(t)}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        activeType === t
                          ? 'bg-foreground-950 text-background-50'
                          : 'bg-background-50 text-foreground-500 hover:bg-background-200 border border-background-200'
                      }`}
                    >
                      {t === 'todos' ? 'Todos' : campaignTypeLabels[t as CampaignType]} ({typeCounts[t] ?? 0})
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1">
                  <span className="text-foreground-400 text-[10px] font-medium self-center mr-1">Estado:</span>
                  {['todos', ...allStatuses].map((s) => (
                    <button
                      key={s}
                      onClick={() => setActiveStatus(s)}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        activeStatus === s
                          ? 'bg-foreground-950 text-background-50'
                          : 'bg-background-50 text-foreground-500 hover:bg-background-200 border border-background-200'
                      }`}
                    >
                      {s === 'todos' ? 'Todos' : campaignStatusLabels[s as CampaignStatus]} ({statusCounts[s] ?? 0})
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Results */}
            {loadingState === 'ready' &&
              (filtered.length === 0 ? (
                <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
                    <i className="ri-inbox-line text-foreground-300 text-2xl"></i>
                  </div>
                  <p className="text-foreground-800 text-sm font-semibold mb-1">Sin resultados</p>
                  <p className="text-foreground-400 text-xs mb-4">No se encontraron campañas con estos filtros.</p>
                  <button
                    onClick={() => {
                      setActiveType('todos');
                      setActiveStatus('todos');
                      setSearchTerm('');
                    }}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-background-200 text-foreground-600 text-xs font-bold hover:bg-background-300 transition-colors cursor-pointer"
                  >
                    <i className="ri-filter-off-line text-sm"></i>
                    Limpiar filtros
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {filtered.map((camp) => {
                    const tc = typeColorMap[camp.type];
                    return (
                      <div
                        key={camp.id}
                        className="bg-background-50 border border-background-200 rounded-2xl p-4 hover:border-background-300 transition-colors group"
                      >
                        {/* Top row */}
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className={`w-9 h-9 rounded-xl ${tc.bg} flex items-center justify-center shrink-0`}>
                              <i className={`${campaignTypeIcon[camp.type]} ${tc.text} text-sm`}></i>
                            </div>
                            <div className="min-w-0">
                              <p className="text-foreground-800 text-xs font-semibold truncate">{camp.name}</p>
                              <p className="text-foreground-400 text-[10px]">{campaignTypeLabels[camp.type]}</p>
                            </div>
                          </div>
                          {statusBadge(camp.status)}
                        </div>

                        {/* Body */}
                        <p className="text-foreground-500 text-[10px] leading-relaxed mb-3 line-clamp-2">{camp.body}</p>

                        {/* Audience */}
                        <div className="flex items-center gap-1.5 mb-3">
                          <span className="text-foreground-400 text-[9px]">
                            <i className="ri-group-line text-[9px] mr-0.5"></i>
                            {camp.audience.label}
                          </span>
                        </div>

                        {/* Metrics grid */}
                        <div className="grid grid-cols-4 gap-1.5 mb-3">
                          <div className="bg-background-100 rounded-lg px-2 py-1.5 text-center">
                            <p className="text-foreground-800 text-xs font-bold">{camp.sentCount}</p>
                            <p className="text-foreground-400 text-[9px]">Env.</p>
                          </div>
                          <div className="bg-background-100 rounded-lg px-2 py-1.5 text-center">
                            <p className="text-foreground-800 text-xs font-bold">{camp.openedCount}</p>
                            <p className="text-foreground-400 text-[9px]">Abie.</p>
                          </div>
                          <div className="bg-background-100 rounded-lg px-2 py-1.5 text-center">
                            <p className="text-foreground-800 text-xs font-bold">{camp.clicks}</p>
                            <p className="text-foreground-400 text-[9px]">Clicks</p>
                          </div>
                          <div className="bg-background-100 rounded-lg px-2 py-1.5 text-center">
                            <p className="text-foreground-800 text-xs font-bold">{camp.convertedCount}</p>
                            <p className="text-foreground-400 text-[9px]">Conv.</p>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1 flex-wrap">
                          {camp.status === 'draft' && (
                            <button
                              onClick={() => handleActivate(camp)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-600 text-[10px] font-medium hover:bg-emerald-100 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-play-line text-[9px]"></i>
                              Activar
                            </button>
                          )}
                          {camp.status === 'scheduled' && (
                            <>
                              <button
                                onClick={() => handleActivate(camp)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-600 text-[10px] font-medium hover:bg-emerald-100 transition-colors cursor-pointer whitespace-nowrap"
                              >
                                <i className="ri-play-line text-[9px]"></i>
                                Activar ahora
                              </button>
                              <button
                                onClick={() => handleStatusChange(camp, 'draft')}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-50 text-amber-600 text-[10px] font-medium hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap"
                              >
                                <i className="ri-close-circle-line text-[9px]"></i>
                                Cancelar prog.
                              </button>
                            </>
                          )}
                          {camp.status === 'active' && (
                            <button
                              onClick={() => handlePause(camp)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-50 text-amber-600 text-[10px] font-medium hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-pause-line text-[9px]"></i>
                              Pausar
                            </button>
                          )}
                          {camp.status === 'paused' && (
                            <button
                              onClick={() => handleActivate(camp)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-600 text-[10px] font-medium hover:bg-emerald-100 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-play-line text-[9px]"></i>
                              Reanudar
                            </button>
                          )}
                          <button
                            onClick={() => setMetricsCampaign(camp)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-background-100 text-foreground-500 text-[10px] font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                            title="Ver métricas"
                          >
                            <i className="ri-bar-chart-line text-[9px]"></i>
                            Métricas
                          </button>
                          <button
                            onClick={() => handleDuplicate(camp)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-background-100 text-foreground-500 text-[10px] font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                            title="Duplicar"
                          >
                            <i className="ri-file-copy-line text-[9px]"></i>
                          </button>
                          <button
                            onClick={() => openEdit(camp)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-background-100 text-foreground-500 text-[10px] font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                            title="Editar"
                          >
                            <i className="ri-pencil-line text-[9px]"></i>
                          </button>
                          {(camp.status === 'draft' || camp.status === 'completed') && (
                            <button
                              onClick={() => handleDelete(camp)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-red-50 text-red-400 text-[10px] font-medium hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap ml-auto"
                              title="Eliminar"
                            >
                              <i className="ri-delete-bin-line text-[9px]"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
          </div>
        </main>
      </div>

      {/* Create / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" onClick={() => setShowForm(false)}>
          <div
            className="bg-background-50 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
              <p className="text-foreground-800 text-sm font-bold">
                {editingCampaign ? 'Editar Campaña' : 'Nueva Campaña'}
              </p>
              <button
                onClick={() => setShowForm(false)}
                className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
              >
                <i className="ri-close-line text-foreground-500 text-sm"></i>
              </button>
            </div>
            <div className="p-5 space-y-3">
              {/* Nombre */}
              <div>
                <label className="text-foreground-400 text-[10px] font-medium block mb-1">Nombre de la campaña</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ej: Upgrade a Pro — Julio 2026"
                  className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
                />
              </div>

              {/* Tipo */}
              <div>
                <label className="text-foreground-400 text-[10px] font-medium block mb-1">Tipo de campaña</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value as CampaignType })}
                  className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors cursor-pointer"
                >
                  {allTypes.map((t) => (
                    <option key={t} value={t}>
                      {campaignTypeLabels[t]}
                    </option>
                  ))}
                </select>
              </div>

              {/* Audiencia */}
              <div>
                <label className="text-foreground-400 text-[10px] font-medium block mb-1">Seleccionar audiencia</label>
                <select
                  value={form.audienceType}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      audienceType: e.target.value as AudienceType,
                      audiencePlan: '',
                      audienceStatus: '',
                      audienceCountry: '',
                    })
                  }
                  className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors cursor-pointer mb-2"
                >
                  {(['all', 'plan', 'status', 'country'] as AudienceType[]).map((a) => (
                    <option key={a} value={a}>
                      {audienceTypeLabels[a]}
                    </option>
                  ))}
                </select>

                {form.audienceType === 'plan' && (
                  <select
                    value={form.audiencePlan}
                    onChange={(e) => setForm({ ...form, audiencePlan: e.target.value })}
                    className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors cursor-pointer"
                  >
                    <option value="">Seleccionar plan...</option>
                    {plans.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                )}

                {form.audienceType === 'status' && (
                  <select
                    value={form.audienceStatus}
                    onChange={(e) => setForm({ ...form, audienceStatus: e.target.value })}
                    className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors cursor-pointer"
                  >
                    <option value="">Seleccionar estado...</option>
                    {radioStatuses.map((s) => (
                      <option key={s} value={s}>
                        {radioStatusLabels[s]}
                      </option>
                    ))}
                  </select>
                )}

                {form.audienceType === 'country' && (
                  <select
                    value={form.audienceCountry}
                    onChange={(e) => setForm({ ...form, audienceCountry: e.target.value })}
                    className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors cursor-pointer"
                  >
                    <option value="">Seleccionar país...</option>
                    {countries.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Contenido */}
              <div>
                <label className="text-foreground-400 text-[10px] font-medium block mb-1">Cuerpo del mensaje</label>
                <textarea
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  rows={4}
                  placeholder="Escribí el contenido del mensaje..."
                  className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors resize-none"
                ></textarea>
              </div>

              {/* Programar */}
              <div>
                <label className="text-foreground-400 text-[10px] font-medium block mb-1">Programar envío (opcional)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={form.scheduledAt}
                    onChange={(e) => setForm({ ...form, scheduledAt: e.target.value })}
                    className="flex-1 px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                  />
                  {form.scheduledAt && (
                    <button
                      onClick={() => setForm({ ...form, scheduledAt: '' })}
                      className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center cursor-pointer hover:bg-red-100 transition-colors shrink-0"
                    >
                      <i className="ri-close-line text-red-400 text-sm"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-background-200">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-bold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                {editingCampaign ? 'Guardar Cambios' : 'Crear Campaña'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Metrics Detail Modal */}
      {metricsCampaign && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4"
          onClick={() => setMetricsCampaign(null)}
        >
          <div
            className="bg-background-50 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
              <div className="flex items-center gap-2.5">
                <div className={`w-8 h-8 rounded-lg ${typeColorMap[metricsCampaign.type].bg} flex items-center justify-center`}>
                  <i className={`${campaignTypeIcon[metricsCampaign.type]} ${typeColorMap[metricsCampaign.type].text} text-sm`}></i>
                </div>
                <div>
                  <p className="text-foreground-800 text-xs font-bold">{metricsCampaign.name}</p>
                  <p className="text-foreground-400 text-[10px]">
                    {campaignTypeLabels[metricsCampaign.type]} · {campaignStatusLabels[metricsCampaign.status]}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setMetricsCampaign(null)}
                className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
              >
                <i className="ri-close-line text-foreground-500 text-sm"></i>
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Audience info */}
              <div className="flex items-center gap-2 text-[10px] text-foreground-500 bg-background-100 rounded-xl px-3 py-2">
                <i className="ri-group-line"></i>
                <span>Audiencia: {metricsCampaign.audience.label}</span>
              </div>

              {/* Progress bar */}
              {metricsCampaign.targetCount > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-foreground-400 text-[10px] font-medium">Progreso de envío</span>
                    <span className="text-foreground-800 text-[10px] font-bold">
                      {metricsCampaign.sentCount}/{metricsCampaign.targetCount}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-background-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-400 rounded-full transition-all duration-500"
                      style={{ width: `${(metricsCampaign.sentCount / metricsCampaign.targetCount) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Main stats grid */}
              <div className="grid grid-cols-4 gap-2">
                <div className="bg-background-100 rounded-xl p-3 text-center">
                  <p className="text-foreground-800 text-lg font-bold">{metricsCampaign.sentCount}</p>
                  <p className="text-foreground-400 text-[10px] font-medium">Enviados</p>
                </div>
                <div className="bg-sky-50 rounded-xl p-3 text-center">
                  <p className="text-sky-700 text-lg font-bold">{metricsCampaign.openedCount}</p>
                  <p className="text-sky-500 text-[10px] font-medium">Abiertos</p>
                </div>
                <div className="bg-violet-50 rounded-xl p-3 text-center">
                  <p className="text-violet-700 text-lg font-bold">{metricsCampaign.clicks}</p>
                  <p className="text-violet-500 text-[10px] font-medium">Clicks</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3 text-center">
                  <p className="text-emerald-700 text-lg font-bold">{metricsCampaign.convertedCount}</p>
                  <p className="text-emerald-500 text-[10px] font-medium">Conversiones</p>
                </div>
              </div>

              {/* Rates */}
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-foreground-500 text-[10px] flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-sky-400"></span>
                    Tasa de apertura
                  </span>
                  <span className="text-foreground-800 text-xs font-bold">{openRate(metricsCampaign)}%</span>
                </div>
                <div className="w-full h-1.5 bg-background-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-sky-400 rounded-full"
                    style={{ width: `${openRate(metricsCampaign)}%` }}
                  ></div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-foreground-500 text-[10px] flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-violet-400"></span>
                    Tasa de clicks
                  </span>
                  <span className="text-foreground-800 text-xs font-bold">{clickRate(metricsCampaign)}%</span>
                </div>
                <div className="w-full h-1.5 bg-background-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-violet-400 rounded-full"
                    style={{ width: `${clickRate(metricsCampaign)}%` }}
                  ></div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-foreground-500 text-[10px] flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                    Tasa de conversión
                  </span>
                  <span className="text-foreground-800 text-xs font-bold">{convRate(metricsCampaign)}%</span>
                </div>
                <div className="w-full h-1.5 bg-background-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-400 rounded-full"
                    style={{ width: `${convRate(metricsCampaign)}%` }}
                  ></div>
                </div>
              </div>

              {/* Funnel */}
              <div className="bg-background-100 rounded-xl p-3">
                <p className="text-foreground-500 text-[10px] font-medium mb-2">Embudo de conversión</p>
                <div className="flex items-end gap-1 h-20">
                  {[
                    { label: 'Env.', value: metricsCampaign.sentCount, max: metricsCampaign.sentCount || 1, color: 'bg-foreground-300' },
                    { label: 'Abie.', value: metricsCampaign.openedCount, max: metricsCampaign.sentCount || 1, color: 'bg-sky-400' },
                    { label: 'Clicks', value: metricsCampaign.clicks, max: metricsCampaign.sentCount || 1, color: 'bg-violet-400' },
                    { label: 'Conv.', value: metricsCampaign.convertedCount, max: metricsCampaign.sentCount || 1, color: 'bg-emerald-400' },
                  ].map((bar) => (
                    <div key={bar.label} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-foreground-800 text-[10px] font-bold">{bar.value}</span>
                      <div
                        className={`w-full ${bar.color} rounded-t-md transition-all duration-500`}
                        style={{ height: `${(bar.value / bar.max) * 100}%` }}
                      ></div>
                      <span className="text-foreground-400 text-[9px]">{bar.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Schedule info */}
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-foreground-400">
                  Creada: {metricsCampaign.createdAt}
                </span>
                {metricsCampaign.scheduledAt && (
                  <span className="text-sky-600 font-medium">
                    Programada: {metricsCampaign.scheduledAt}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-background-200">
              <button
                onClick={() => setMetricsCampaign(null)}
                className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-bold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cerrar
              </button>
              <button
                onClick={() => {
                  setMetricsCampaign(null);
                  openEdit(metricsCampaign);
                }}
                className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                Editar Campaña
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile bottom bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">Super Admin · Marketing</p>
      </div>
    </div>
  );
}